import type Uppy from "@uppy/core"
import { type FailedUppyFile, type UploadedUppyFile, type UppyFile } from "@uppy/core"
import { DragDrop } from "@uppy/react"
import cn from "classnames"
import React, { useCallback, useEffect, useState } from "react"
import { type UseFormRegister } from "react-hook-form"
import { useTranslation } from "react-i18next"
import invariant from "tiny-invariant"
import { type InvalidUppyFile, useUppy } from "@/common/hooks/use_uppy"
import { CheckmarkFill, CloseLarge, ErrorFill, LoadingCircle } from "@/skylticons"

interface UploaderFileProps {
  uppy: Uppy
  file: UploadedUppyFile<any, any> | FailedUppyFile<any, any>
  name?: string
  formRegister?: UseFormRegister<any>
  index?: number
}

const UploaderFile: React.FC<UploaderFileProps> = ({ uppy, file, name, formRegister, index }) => {
  const [fileData, setFileData] = useState<any>()

  useEffect(() => {
    const success = (uploaded: UppyFile, response: any) => {
      if (file.id !== uploaded.id) {
        return
      }

      const uploadedFileData = {
        id: response.uploadURL,
        storage: "cache",
        metadata: {
          size: uploaded.size,
          filename: uploaded.name,
          mime_type: uploaded.type,
        },
      }

      setFileData(JSON.stringify(uploadedFileData))
    }

    uppy.on("upload-success", success)

    return () => {
      uppy.off("upload-success", success)
    }
  }, [uppy, file])

  const done = !!fileData

  let icon = <LoadingCircle className="loading" />

  if (done) {
    if (file?.preview) {
      icon = <img src={file.preview} />
    } else {
      icon = <CheckmarkFill />
    }
  }

  const inputName = `${name || "file"}[${index}]`
  const registerFields = formRegister && formRegister(inputName)

  return (
    <div className={cn("uploader-item", { done, uploading: !done })}>
      <div className="progress" style={{ width: `${file.progress?.percentage || 0}%` }}></div>
      <div data-hj-suppress className="icon">
        {icon}
      </div>
      <div className="info">
        <div className="name data-hj-suppress">{file.name}</div>
      </div>
      <div className="buttons">
        <button
          className="delete"
          onClick={e => {
            e.stopPropagation()
            uppy.removeFile(file.id)
          }}
          type="button"
        >
          <CloseLarge />
        </button>
      </div>
      {fileData && <input type="hidden" value={fileData} name={inputName} {...registerFields} />}
    </div>
  )
}

interface InvalidImageProps {
  file: InvalidUppyFile
  removeFile: (file: InvalidUppyFile) => void
}

const InvalidImage: React.FC<InvalidImageProps> = ({ file, removeFile }) => {
  const deleteImage = useCallback(() => {
    removeFile(file)
  }, [removeFile, file])

  return (
    <>
      <div className="uploader-item uploader-item-invalid">
        <div className="icon">
          <ErrorFill />
        </div>
        <div className="info">
          <div className="name">{file.name}</div>
        </div>
        <div className="buttons">
          <button
            className="delete"
            onClick={e => {
              e.stopPropagation()
              deleteImage()
            }}
            type="button"
          >
            <CloseLarge />
          </button>
        </div>
      </div>
      <div className="uploader-error paragraph-2xs mb-0">{file.error}</div>
    </>
  )
}

export interface UploaderProps {
  allowedTypes?: string[]
  name?: string
  formRegister?: UseFormRegister<any>
}

const Uploader: React.FC<UploaderProps> = ({ allowedTypes, name, formRegister }) => {
  const { t } = useTranslation()
  const { uppy, state: uppyState, invalidFiles, removeInvalidFile } = useUppy("uploader", allowedTypes, true)

  const files: Array<UploadedUppyFile<any, any> | FailedUppyFile<any, any>> = []

  for (const key in uppyState.files) {
    if (Object.prototype.hasOwnProperty.call(uppyState.files, key)) {
      const file = uppyState.files[key]
      invariant(file)
      files.push(file)
    }
  }

  const extensions = allowedTypes?.filter(t => !t.includes("/")) || []
  const formats = extensions.slice(0, extensions.length - 1).join(", ")
  const lastFormat = extensions.pop()

  return (
    <>
      {allowedTypes && (
        <p className="uploader-label paragraph-xs">{t("uploader.label", { formats, lastFormat, maxSize: "25mb" })}</p>
      )}
      <DragDrop
        uppy={uppy}
        height="96"
        locale={{
          strings: {
            dropHereOr: "%{browse}",
            browse: t("uploader.browse"),
          },
        }}
      />
      {invalidFiles.map(file => (
        <InvalidImage key={file.id} file={file} removeFile={removeInvalidFile} />
      ))}
      {files.map((f, index) => (
        <UploaderFile key={f.id} uppy={uppy} file={f} name={name} formRegister={formRegister} index={index} />
      ))}
    </>
  )
}

export default Uploader
